import { AfterViewChecked, Component, ElementRef, OnInit, OnDestroy } from '@angular/core';
import { ProductPreview } from '../../models/product-preview.models';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Subscription, timer } from 'rxjs';
import { take, shareReplay } from 'rxjs/operators';
import { DwComponent, DwComponentType, DwMessage, DwMessageSeverity, DwSecurityUserService } from '@devwareapps/devware-cap';
import { DwProductEntity, DwProductSizeEntity, DwCodeValueEntity } from '../../../../meta-data/app-meta-data.service';
import { ChallengeCatalog, FitnessReferenceDataService, RegistrationOptions, challengeRegistrationPrice } from '../../services/fitness-reference-data.service';
import { FitnessCommerceService } from '../../../commerce/services/fitness-commerce.service';
import { CartItemInput, CartLine, CheckoutConfiguration, CommerceProduct, ProductSelection } from '../../../commerce/models/commerce.models';
import { SummaryDraftLine } from '../../../commerce/components/order-summary/order-summary.component';

interface ParticipantDraft {
    firstName: string; lastName: string; email: string; years: number; sourceId: number; isBuyer: boolean;
    sourceName: string; referralCode: string; teamCode: string; swagId: number; sizeId: number;
    selection: ProductSelection;
    lookupEmail?: string; lookupState?: 'loading' | 'registered' | 'previous' | 'found' | 'new' | 'error'; lookupRequest?: Subscription;
    lookupDelay?: Subscription;
}
interface ProductDraft { product: DwProductEntity; quantity: number; sizeId: number; selection: ProductSelection; }
interface DraftCommand { item?: CartItemInput; itemId?: string; removeId?: string; saved: () => void; }

@DwComponent({ key: 'fitness-challenge-registration', name: 'Challenge Registration',
    componentType: DwComponentType.pageComponent, isGlobal: true })
@Component({ selector: 'app-challenge-registration', templateUrl: './challenge-registration.component.html',
    styleUrls: ['./challenge-registration.component.scss'] })
export class ChallengeRegistrationComponent implements OnInit, OnDestroy, AfterViewChecked {
    catalog: ChallengeCatalog;
    sizes: DwProductSizeEntity[] = [];
    participants: ParticipantDraft[] = [];
    merchandise: ProductDraft[] = [];
    donations: DwProductEntity[] = [];
    donationProductId = 0;
    donationAmount = 0;
    yearsOptions: RegistrationOptions['years'] = [];
    sourceOptions: DwCodeValueEntity[] = [];
    private buyerAdded = false;
    alreadyRegistered = false;
    private buyerYears: number = null;
    productPreviews: { [id: number]: ProductPreview } = {};
    openPreviewId = 0;
    dismissedPreviewId = 0;
    get registrationPrice(): number { return challengeRegistrationPrice(this.catalog && this.catalog.challenge); }
    get buyer() { return this.security.securityContext && this.security.securityContext.ApplicationUser; }
    loading = true;
    error = '';
    get pageMessage(): DwMessage {
        const retry = this.draftPending && !this.draftSaving
            ? 'Your selections could not be saved. Select Try again to continue.' : '';
        if (this.error || retry) {
            return { severity: this.error ? DwMessageSeverity.error : DwMessageSeverity.warning,
                messageBody: [this.error, retry].filter(Boolean).join(' '), allowClose: false };
        }
        if (this.catalog && !this.loading && (!this.storeOpen || !this.registrationOpen)) {
            return { severity: DwMessageSeverity.info, allowClose: false,
                messageBody: !this.storeOpen ? 'This challenge store is closed.'
                    : 'Registration is closed. You can still shop or donate while the store is open.' };
        }
        return null;
    }
    reviewed = false;
    donationPreferencesBusy = false;
    private sub = new Subscription();
    private referralCode = '';
    private teamCode = '';
    commerceProducts: CommerceProduct[] = [];
    checkoutConfiguration: CheckoutConfiguration;
    commerceReady = false;
    draftSaving = false;
    draftPending = false;
    private queue: DraftCommand[] = [];
    private creatingCart = false;
    private checkoutAfterSave = false;
    private checkoutNavigating = false;
    private _focusShirt = false;
    private promotionAfterSave: string = null;
    get summaryDrafts(): SummaryDraftLine[] {
        const rows: SummaryDraftLine[] = this.registrationOpen ? this.participants.map((p, i) => ({ key: i,
            name: [p.firstName, p.lastName].join(' ').trim() || 'Streaker ' + (i + 1),
            included: p.swagId ? [this.includedShirtDescription(p)] : [],
            amount: this.registrationPrice + (p.swagId ? this.sizeCost(p.selection.productSizeId || p.sizeId) : 0) })) : [];
        if (+this.donationAmount > 0 && !this.savedDonation) { rows.push({ key: 'donation', name: 'Streaker Family Donation', amount: +this.donationAmount }); }
        return rows;
    }
    private includedShirtDescription(p: ParticipantDraft): string {
        const product = this.commerceProduct(p.swagId);
        const size = product?.sizeLabels?.[p.selection.productSizeId || p.sizeId];
        const name = product?.name || this.selectedSwag(p)?.ProductName || 'Shirt';
        return name + (size ? ' - ' + size : '');
    }
    removeSummaryDraft(key: number | string): void { if (key === 'donation') { this.setDonation(0); } else { this.removeParticipant(+key); } }
    applyPromotion(code: string, valid: boolean): void {
        if (!this.canMutate) { return; }
        if (!code && this.commerce.cart) { this.savePromotion(''); return; }
        this.promotionAfterSave = code;
        if (this.summaryDrafts.length) {
            this.saveDraft(valid);
            if (!this.draftSaving && !this.draftPending && this.summaryDrafts.length) { this.promotionAfterSave = null; }
        } else if (this.commerce.cart) { this.savePromotion(code); this.promotionAfterSave = null; }
        else { this.promotionAfterSave = null; this.error = 'Add an item to your order before applying a code.'; }
    }
    private savePromotion(code: string): void {
        this.draftSaving = true; this.error = '';
        this.sub.add(this.commerce.promotion(code).subscribe(() => {
            this.draftSaving = false;
            if (this.commerce.cart?.cart.quote?.issues.some(i => i.startsWith('promotion_'))) {
                this.error = 'This discount code does not apply to your order. Change or remove it.';
            }
        }, error => this.saveFailed(error)));
    }
    get visibleMerchandise(): ProductDraft[] {
        return this.merchandise.filter(line => this.registrationOpen ||
            !(/shirt/i.test(line.product.ProductName) || this.includedProducts.some(p => p.ProductId === line.product.ProductId)));
    }
    get savedLines() { return this.commerce.cart && this.commerce.cart.cart ? this.commerce.cart.cart.lines.filter(line => !line.parentItemId) : []; }
    get savedDonation(): CartLine { return this.savedLines.find(line => line.input.kind === 'donation'); }
    get familyDonation(): DwProductEntity {
        return this.donations.find(p => /streaker.*family/i.test(p.ProductName)) || (this.donations.length === 1 ? this.donations[0] : undefined);
    }
    lineName(line: CartLine): string {
        const p = line.input.participant;
        return p ? [p.firstName, p.lastName].join(' ') : line.input.kind === 'donation' ? 'Streaker Family Donation' : line.description;
    }
    lineTotal(line: CartLine): number {
        return line.unitPrice * line.input.quantity + (this.commerce.cart?.cart.lines || [])
            .filter(child => child.parentItemId === line.itemId).reduce((sum, child) => sum + child.unitPrice * child.input.quantity, 0);
    }
    sourceDetailLabel(p: ParticipantDraft): string {
        const source = this.sourceOptions.find(s => s.CodeValueId === +p.sourceId);
        switch ((source && source.Meaning || '').toLowerCase()) {
            case 'challenge': return 'Who challenged you?';
            case 'individual': return "Individual's name";
            case 'other': return 'Other source';
            default: return '';
        }
    }
    removeSaved(line: CartLine): void {
        if (!this.canMutate) { return; }
        if (line.input.kind === 'donation') { this.setDonation(0); return; }
        this.startCommands([{ removeId: line.itemId, saved: () => {} }]);
    }
    private get canMutate(): boolean {
        return this.storeOpen && this.commerceReady && !this.cartLocked && !this.draftSaving && !this.draftPending && !this.commerce.retryRequired;
    }
    setDonation(amount: number): void {
        if (!this.canMutate) { return; }
        amount = +amount;
        if (!Number.isFinite(amount) || amount < 0 || amount > 100000 || Math.abs(amount * 100 - Math.round(amount * 100)) > .000001) {
            this.error = 'Choose a valid donation amount.'; return;
        }
        const product = this.familyDonation;
        if (!product) { return; }
        this.error = '';
        this.donationAmount = amount; this.donationProductId = product.ProductId;
        const existing = this.savedLines.filter(line => line.input.kind === 'donation');
        const commands: DraftCommand[] = existing.slice(amount > 0 ? 1 : 0).map(line => ({ removeId: line.itemId, saved: () => {} }));
        if (amount > 0) {
            commands.push({ item: { kind: 'donation', productId: product.ProductId, quantity: 1, donationAmount: amount, deliveryMethod: 'pickup' },
                itemId: existing[0] && existing[0].itemId, saved: () => {} });
        }
        if (commands.length) { this.startCommands(commands); }
    }
    private startCommands(commands: DraftCommand[]): void {
        this.error = ''; this.queue = commands; this.draftSaving = true; this.draftPending = true;
        if (!this.commerce.cart) {
            this.creatingCart = true;
            this.sub.add(this.commerce.create().subscribe(() => { this.creatingCart = false; this.rememberOrder(); this.saveNext(); }, error => this.saveFailed(error)));
        } else { this.saveNext(); }
    }
    checkout(valid: boolean): void {
        if (this.donationPreferencesBusy) { return; }
        if (!this.canMutate || this.checkoutNavigating) { return; }
        if (!valid) { this.error = 'Please complete the required fields.'; return; }
        this.checkoutAfterSave = true; this.saveDraft(valid);
        if (!this.draftSaving && !this.draftPending) { this.checkoutAfterSave = false; }
    }
    addItem(item: CartItemInput, saved: () => void): void {
        if (!this.storeOpen || !this.commerceReady || this.cartLocked || this.draftSaving || this.draftPending || this.commerce.retryRequired) { return; }
        if (!Number.isInteger(item.quantity) || item.quantity < 1 || item.quantity > 100 || !this.validSelection(item) ||
            item.kind === 'donation' && (!Number.isFinite(item.donationAmount) || item.donationAmount <= 0 || item.donationAmount > 100000)) {
            this.error = 'Choose the required options and a valid quantity or donation amount.'; return;
        }
        this.startCommands([{ item, saved }]);
    }
    addProduct(line: ProductDraft): void {
        this.addItem({ ...line.selection, deliveryMethod: 'mail', kind: 'product', productId: line.product.ProductId, quantity: +line.quantity || 1 }, () => line.quantity = 0);
    }
    addDonation(): void {
        this.setDonation(this.donationAmount);
    }
    constructor(private data: FitnessReferenceDataService, private route: ActivatedRoute,
        public commerce: FitnessCommerceService, private router: Router, private security: DwSecurityUserService,
        private _element: ElementRef<HTMLElement>) {}

    ngAfterViewChecked(): void {
        if (!this._focusShirt || this.loading || !this.commerceReady) return;
        const productId = +this.route.snapshot.queryParamMap.get('product');
        const requested = this.visibleMerchandise.find(line => line.product.ProductId === productId);
        const scope = requested ? `.extras [data-product-id="${requested.product.ProductId}"]` : '.extras';
        const size = this._element?.nativeElement.querySelector<HTMLSelectElement>(scope + ' select[data-product-size]');
        this._focusShirt = false;
        if (size) { size.scrollIntoView({block: 'center'}); size.focus({preventScroll: true}); }
    }

    ngOnInit(): void {
        this.sub.add(this.commerce.cart$.subscribe(cart => {
            if (cart) { this.rememberOrder(); }
            if (!this.draftSaving) { this.donationAmount = this.savedDonation ? this.savedDonation.input.donationAmount || 0 : 0; }
        }));
        this.sub.add(this.commerce.accountChanged$.subscribe(() => {
            this.queue = []; this.participants = []; this.merchandise.forEach(line => line.quantity = 0);
            this.donationAmount = 0; this.commerceReady = false; this.draftSaving = false; this.draftPending = false;
            this.catalog = null; this.commerceProducts = []; this.checkoutConfiguration = null;
            this.referralCode = ''; this.teamCode = ''; this.buyerAdded = false; this.alreadyRegistered = false;
            this.promotionAfterSave = null;
            this.buyerYears = null; this.productPreviews = {}; this.openPreviewId = 0; this.dismissedPreviewId = 0;
            this.error = 'Your account changed. Reload this page to load the correct catalog and order.';
        }));
        const params = this.route.snapshot.queryParamMap;
        const attribution = this.commerce.registrationAttribution(params.get('challenge-code') || params.get('challenge_code'),
            params.get('team_code') || params.get('team-code'));
        this.referralCode = attribution.referralCode; this.teamCode = attribution.teamCode;
        const productsSource=this.data.getProducts().pipe(take(1),shareReplay(1));
        const orderId=+this.route.snapshot.queryParamMap.get('order');
        const referenceSource=combineLatest([this.data.getActiveChallengeCatalog(), productsSource,
            this.data.getProductSizes(), this.data.getProductTypes(), this.data.getRegistrationOptions(), this.data.getBuyerChallengeHistory()]).pipe(take(1));
        const checkoutSource=combineLatest([this.commerce.products(),this.commerce.configuration(),
            this.commerce.restore(Number.isInteger(orderId) && orderId>0 ? orderId : undefined)]).pipe(take(1));
        this.sub.add(combineLatest([referenceSource,checkoutSource]).pipe(take(1))
            .subscribe(([[catalog, products, sizes, types, options, history], [commerceProducts, config]]) => {
                this.catalog = catalog;
                this.alreadyRegistered = history.some(row => row.ChallengeDefinitionId === catalog.challenge.ChallengeDefinitionId && !!row.ChallengeDefinitionId);
                this.buyerAdded = this.alreadyRegistered;
                this.yearsOptions = options.years; this.sourceOptions = options.sources;
                const previous = history.find(row => this.yearsOptions.some(option => Number(option.Id) === row.YearsStreakingCv));
                this.buyerYears = previous ? previous.YearsStreakingCv : null;
                this.sizes = sizes;
                const type = (p: DwProductEntity) => (types.find(t => t.ProductTypeId === p.ProductTypeId) || {}).ProductType;
                this.merchandise = products.filter(p => type(p) === 'Gear' && this.validPrice(p))
                    .map(product => ({ product, quantity: 0, sizeId: 0,
                        selection: { productId: product.ProductId, deliveryMethod: 'mail' as 'mail' } }));
                this.donations = products.filter(p => type(p) === 'Donation');
                this.donationProductId = this.familyDonation ? this.familyDonation.ProductId : 0;
                const intent = this.route.snapshot.queryParamMap.get('intent');
                this.loading = false;
                this.sub.add(this.data.getProductPreviews(products.map(p=>p.ProductId))
                    .subscribe(previews=>this.productPreviews=previews,()=>{}));
                this.acceptCommerce(commerceProducts,config);
                if (this.registrationOpen && (intent === 'register' || !this.alreadyRegistered && intent !== 'donate' && intent !== 'store')) { this.addParticipant(); }
                this._focusShirt = intent === 'store';
                if (!orderId && ['register', 'store', 'donate'].includes(intent) && this.canStartOrder) {
                    this.startOrder();
                } else { this.applyDonationIntent(); }
            }, () => { this.error = 'Unable to load registration products. Please reload.'; this.loading = false; }));
    }
    ngOnDestroy(): void { this.sub.unsubscribe(); }

    loadCommerce(): void {
        this.error = '';
        const orderId = +this.route.snapshot.queryParamMap.get('order');
        this.sub.add(combineLatest([this.commerce.products(), this.commerce.configuration(),
            this.commerce.restore(Number.isInteger(orderId) && orderId > 0 ? orderId : undefined)]).pipe(take(1))
            .subscribe(([products, config]) => {
                this.acceptCommerce(products,config);
            }, error => this.reportCommerceError(error)));
    }
    private acceptCommerce(products: CommerceProduct[], config: CheckoutConfiguration): void {
        this.commerceProducts = products; this.checkoutConfiguration = config; this.commerceReady = true;
        const email = this.buyer && this.buyer.Email || '';
        if (email && this.savedLines.some(line => line.input.kind === 'registration' && line.input.participant.email.toLowerCase() === email.toLowerCase())) {
            this.participants = this.participants.filter(p => !p.isBuyer); this.buyerAdded = true;
        }
        this.rememberOrder();
    }
    get cartLocked(): boolean { return !!this.commerce.cart && this.commerce.cart.state !== 'Cart'; }
    private applyDonationIntent(): void {
        if (this.route.snapshot.queryParamMap.get('intent') === 'donate' && this.storeOpen &&
            !this.cartLocked && this.familyDonation) {
            this.donationAmount = this.savedDonation ? this.savedDonation.input.donationAmount || 0 : 25;
        }
    }
    get canStartOrder(): boolean {
        return this.storeOpen && this.commerceReady && this.cartLocked &&
            this.commerce.cart.state !== 'Pending' && !this.commerce.retryRequired && !this.draftPending;
    }
    startOrder(): void {
        if (!this.canStartOrder || this.draftSaving) { return; }
        this.draftSaving = true; this.draftPending = true; this.creatingCart = true; this.error = '';
        this.sub.add(this.commerce.create().subscribe(() => {
            this.draftSaving = false; this.draftPending = false; this.creatingCart = false;
            this.donationAmount = 0; this.merchandise.forEach(line => line.quantity = 0);
            this.acceptCommerce(this.commerceProducts, this.checkoutConfiguration);
            this.applyDonationIntent();
        }, error => this.saveFailed(error)));
    }
    changePendingOrder(keepItems: boolean): void {
        if (this.draftSaving || this.commerce.busy || this.commerce.retryRequired) { return; }
        this.draftSaving = true; this.error = '';
        this.sub.add(this.commerce.cancelOrder(keepItems).subscribe(() => {
            this.draftSaving = false; this.draftPending = false; this.participants = [];
            this.donationAmount = this.savedDonation?.input.donationAmount || 0;
            this.merchandise.forEach(line => line.quantity = 0);
            this.reviewed = false;
            this.acceptCommerce(this.commerceProducts, this.checkoutConfiguration);
        }, error => { this.draftSaving = false; this.reportCommerceError(error); }));
    }
    commerceProduct(id: number): CommerceProduct { return this.commerceProducts.find(p => p.productId === +id); }
    changeSwag(p: ParticipantDraft): void {
        p.sizeId = 0; p.selection = { productId: +p.swagId, deliveryMethod: 'mail' }; this.reviewed = false;
    }
    saveDraft(valid: boolean): void {
        if (this.draftSaving || this.draftPending || !this.commerceReady || this.cartLocked || this.commerce.retryRequired) { return; }
        this.review(valid);
        if (!this.reviewed) { return; }
        const entries: DraftCommand[] = [];
        if (this.registrationOpen) {
            this.participants.forEach(p => entries.push({ item: {
                kind: 'registration', productId: this.catalog.registrationProduct.ProductId, quantity: 1, deliveryMethod: 'pickup',
                participant: { challengeDefinitionId: this.catalog.challenge.ChallengeDefinitionId,
                    firstName: p.firstName.trim(), lastName: p.lastName.trim(), email: p.email.trim(), yearsStreaking: +p.years,
                    marketingSourceCv: +p.sourceId || undefined, marketingSourceOther: p.sourceName.trim() || undefined, challengeCodeUsed: p.referralCode.trim() || undefined,
                    teamJoinCode: p.teamCode.trim() || undefined },
                swag: p.swagId ? { ...p.selection, productId: +p.swagId, productSizeId: p.selection.productSizeId || p.sizeId || undefined } : undefined,
            }, saved: () => this.participants.splice(this.participants.indexOf(p), 1) }));
        }
        if (+this.donationAmount > 0 && !this.savedDonation) { entries.push({ item: { kind: 'donation', productId: +this.donationProductId,
            quantity: 1, donationAmount: +this.donationAmount, deliveryMethod: 'pickup' }, saved: () => {} }); }
        if (entries.some(e => !this.validSelection(e.item) || e.item.swag && !this.validSelection(e.item.swag))) {
            this.error = 'Choose the required product sizes, colors and options.'; return;
        }
        this.startCommands(entries);
    }
    private validSelection(selection: ProductSelection): boolean {
        const p = this.commerceProduct(selection.productId);
        return !!p && p.active && (!p.hasSizes || Object.prototype.hasOwnProperty.call(p.sizes, selection.productSizeId)) &&
            (!p.colors.length || p.colors.includes(selection.productColorId)) &&
            (!Object.keys(p.options).length || Object.prototype.hasOwnProperty.call(p.options, selection.productOptionDetailId));
    }
    private saveNext(): void {
        if (!this.queue.length) {
            this.draftSaving = false; this.draftPending = false; this.reviewed = false;
            this.donationAmount = this.savedDonation ? this.savedDonation.input.donationAmount || 0 : 0;
            if (this.promotionAfterSave !== null) {
                const code = this.promotionAfterSave; this.promotionAfterSave = null; this.savePromotion(code); return;
            }
            if (this.checkoutAfterSave) {
                this.checkoutAfterSave = false;
                this.checkoutNavigating = true;
                this.router.navigate(['/checkout']).then(navigated => {
                    if (!navigated) { this.checkoutNavigating = false; }
                }, () => {
                    this.checkoutNavigating = false;
                    this.error = 'Checkout could not open. Select Checkout to try again.';
                });
            }
            return;
        }
        const command = this.queue[0];
        const request = command.removeId ? this.commerce.remove(command.removeId) : this.commerce.saveItem(command.item, command.itemId);
        this.sub.add(request.subscribe(() => {
            this.queue.shift().saved(); this.saveNext();
        }, error => this.saveFailed(error)));
    }
    retryDraft(): void {
        if (this.draftSaving || !this.draftPending) { return; }
        this.draftSaving = true; this.error = '';
        this.sub.add(this.commerce.retry().subscribe(() => {
            const startedOrder = this.creatingCart && !this.queue.length;
            if (this.creatingCart) { this.creatingCart = false; this.rememberOrder(); }
            else if (this.queue.length) { this.queue.shift().saved(); }
            this.saveNext();
            if (startedOrder) { this.applyDonationIntent(); }
        }, error => this.saveFailed(error)));
    }
    private saveFailed(error: any): void {
        this.draftSaving = false; this.draftPending = this.commerce.retryRequired;
        if (!this.draftPending) {
            this.queue = []; this.creatingCart = false; this.checkoutAfterSave = false;
            this.promotionAfterSave = null;
            this.donationAmount = this.savedDonation ? this.savedDonation.input.donationAmount || 0 : 0;
        }
        this.reportCommerceError(error);
    }
    private rememberOrder(): void {
        // Checkout restores the cart before this page is destroyed. Do not cancel that
        // navigation with a competing store query-parameter update.
        if (this.checkoutAfterSave || this.checkoutNavigating) { return; }
        if (this.commerce.cart) { this.router.navigate([], { relativeTo: this.route,
            queryParams: { order: this.commerce.cart.orderId > 0 ? this.commerce.cart.orderId : null }, queryParamsHandling: 'merge', replaceUrl: true }); }
    }
    private reportCommerceError(error: any): void {
        const p = error && error.error;
        this.error = p && (p.detail || p.title || p.message) || error.message || 'Unable to save your order.';
    }

    private inWindow(start: string, end: string): boolean {
        const now = Date.now();
        return !!start && !!end && Number.isFinite(Date.parse(start)) && Number.isFinite(Date.parse(end)) &&
            now >= Date.parse(start) && now <= Date.parse(end);
    }
    get storeOpen(): boolean { const c = this.catalog && this.catalog.challenge;
        return !!c && this.inWindow(c.StoreStartDate, c.StoreEndDate); }
    get registrationOpen(): boolean { const c = this.catalog && this.catalog.challenge;
        return this.storeOpen && !!c && this.inWindow(c.RegistrationStartDate, c.RegistrationEndDate); }
    get includedProducts(): DwProductEntity[] {
        return this.catalog ? this.catalog.products.filter(p => p.includedInChallenge && this.validPrice(p.product)).map(p => p.product) : [];
    }
    validPrice(p: DwProductEntity): boolean { return p.ProductDefaultPrice != null &&
        Number.isFinite(p.ProductDefaultPrice) && p.ProductDefaultPrice >= 0; }
    productSizes(id: number): DwProductSizeEntity[] { return this.sizes.filter(s => s.ProductId === +id); }
    selectedSwag(p: ParticipantDraft): DwProductEntity { return this.includedProducts.find(s => s.ProductId === +p.swagId); }
    sizeCost(id: number): number { const s = this.sizes.find(v => v.ProductSizeId === +id); return s ? s.ExtraCost || 0 : 0; }
    private get firstYear(): number {
        const option = this.yearsOptions[0];
        return option ? Number(option.Id) : null;
    }
    addParticipant(): void {
        if (!this.registrationOpen) { return; }
        const isBuyer = !this.buyerAdded;
        const buyer = isBuyer && this.buyer;
        this.buyerAdded = true;
        this.participants.push({ firstName: buyer ? buyer.FirstName || '' : '', lastName: buyer ? buyer.LastName || '' : '',
            email: buyer ? buyer.Email || '' : '', years: isBuyer ? this.buyerYears : this.firstYear,
            sourceId: isBuyer ? (this.sourceOptions.find(source => (source.Meaning || '').toUpperCase() === 'PREVIOUS') || {}).CodeValueId || null : null, isBuyer, sourceName: '',
            referralCode: this.referralCode, teamCode: this.teamCode, swagId: 0, sizeId: 0,
            selection: { productId: 0, deliveryMethod: 'mail' } }); this.reviewed = false;
    }
    removeParticipant(index: number): void {
        this.participants[index].lookupDelay?.unsubscribe();
        this.participants[index].lookupRequest?.unsubscribe();
        this.participants.splice(index, 1); this.reviewed = false;
    }
    participantEmailChanged(p: ParticipantDraft): void {
        p.lookupDelay?.unsubscribe();
        p.lookupRequest?.unsubscribe(); p.lookupEmail = null; p.lookupState = null;
        p.years = p.isBuyer ? this.buyerYears : this.firstYear; p.sourceId = null; p.sourceName = '';
        this.reviewed = false;
        const email = p.email.trim().toLowerCase();
        if (p.isBuyer || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { return; }
        if (this.alreadyRegistered && email === (this.buyer?.Email || '').trim().toLowerCase()) {
            p.lookupEmail = email; p.lookupState = 'registered'; return;
        }
        p.lookupState = 'loading';
        p.lookupDelay = timer(400).subscribe(() => this.lookupParticipant(p));
        this.sub.add(p.lookupDelay);
    }
    lookupParticipant(p: ParticipantDraft): void {
        p.lookupDelay?.unsubscribe();
        const email = p.email.trim().toLowerCase();
        if (!this.participants.includes(p) || !this.catalog) { return; }
        if (!p.isBuyer && this.alreadyRegistered && email === (this.buyer?.Email || '').trim().toLowerCase()) {
            p.lookupEmail = email; p.lookupState = 'registered'; return;
        }
        if (p.isBuyer || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || p.lookupEmail === email) { return; }
        p.lookupRequest?.unsubscribe(); p.lookupEmail = email; p.lookupState = 'loading';
        const active = () => this.participants.includes(p) && p.email.trim().toLowerCase() === email && !!this.catalog;
        p.lookupRequest = this.commerce.lookupParticipant(email, this.catalog.challenge.ChallengeDefinitionId).subscribe(result => {
            if (!active()) { return; }
            p.lookupState = result.alreadyRegistered ? 'registered' : result.found ? result.previousStreaker ? 'previous' : 'found' : 'new';
            if (result.found) {
                p.firstName = result.firstName || ''; p.lastName = result.lastName || '';
                if (this.yearsOptions.some(o => Number(o.Id) === result.yearsStreakingCv)) {
                    p.years = result.yearsStreakingCv;
                }
                if (result.previousStreaker) {
                    p.sourceId = (this.sourceOptions.find(o => (o.Meaning || '').toUpperCase() === 'PREVIOUS') || {}).CodeValueId || null;
                    p.sourceName = '';
                }
            }
            this.reviewed = false;
        }, () => { if (active()) { p.lookupState = 'error'; p.lookupEmail = null; } });
        this.sub.add(p.lookupRequest);
    }
    get subtotal(): number {
        if (!this.catalog) { return 0; }
        return (this.registrationOpen ? this.participants.reduce((sum, p) =>
            sum + this.registrationPrice + (p.swagId ? this.sizeCost(p.selection.productSizeId || p.sizeId) : 0), 0) : 0) +
            (this.savedDonation ? 0 : +this.donationAmount || 0);
    }
    review(valid: boolean): void {
        this.error = ''; this.reviewed = false;
        if (this.participants.some(p => p.lookupState === 'loading')) { this.error = 'Please wait for the streaker lookup to finish.'; return; }
        if (this.participants.some(p => p.lookupState === 'registered')) { this.error = 'A streaker is already registered for this challenge. Remove them or use a different email.'; return; }
        if (!this.storeOpen) { this.error = 'This challenge store is closed.'; return; }
        if (!valid) { this.error = 'Please complete the required fields.'; return; }
        if (this.registrationOpen && this.participants.some(p => !this.yearsOptions.some(o => Number(o.Id) === +p.years) ||
                p.sourceId != null && !this.sourceOptions.some(o => o.CodeValueId === +p.sourceId) ||
                this.sourceDetailLabel(p) && !p.sourceName.trim()) ||
            !Number.isFinite(+this.donationAmount) || +this.donationAmount < 0 || +this.donationAmount > 100000 ||
            Math.abs(+this.donationAmount * 100 - Math.round(+this.donationAmount * 100)) > 0.000001 ||
            this.registrationOpen && this.participants.some(p => !p.firstName.trim() || !p.lastName.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.email.trim()))) {
            this.error = 'Check participant names, whole-number quantities and years, and the donation amount.'; return;
        }
        const emails = this.participants.map(p => p.email.trim().toLowerCase()).concat(this.savedLines
            .filter(line => line.input.kind === 'registration').map(line => line.input.participant.email.trim().toLowerCase()));
        if (this.registrationOpen && new Set(emails).size !== emails.length) {
            this.error = 'Use a different email address for each participant.'; return;
        }
        const hasSize = (id: number, size: number) => this.productSizes(id).some(s => s.ProductSizeId === +size);
        if (this.registrationOpen && this.participants.some(p => this.selectedSwag(p) &&
            this.selectedSwag(p).HasSizes && !hasSize(p.swagId, p.selection.productSizeId || p.sizeId))) {
            this.error = 'Choose a size for each selected shirt or product.'; return;
        }
        if (+this.donationAmount > 0 && !this.donations.some(p => p.ProductId === +this.donationProductId)) {
            this.error = 'Choose a donation product.'; return;
        }
        if (!(this.registrationOpen && this.participants.length) &&
            !(+this.donationAmount > 0) && !this.savedLines.length) {
            this.error = 'Add a participant, product or donation to review your order.'; return;
        }
        this.reviewed = true;
    }
}



